import React, { useState, useEffect } from 'react';
import { Heart, Plus, X, Check, Users, Newspaper, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Available sources to follow
const AVAILABLE_SOURCES = [
  { id: 'bbc', name: 'BBC News', category: 'news', icon: '🌐' },
  { id: 'nytimes', name: 'NY Times', category: 'news', icon: '📰' },
  { id: 'techcrunch', name: 'TechCrunch', category: 'tech', icon: '💻' },
  { id: 'wired', name: 'Wired', category: 'tech', icon: '🔌' },
  { id: 'ktpress', name: 'KT Press', category: 'rwanda', icon: '🇷🇼' },
  { id: 'newtimes', name: 'The New Times', category: 'rwanda', icon: '🇷🇼' },
];

// Topics to follow
const AVAILABLE_TOPICS = [
  { id: 'world', name: 'World News', icon: <Globe className="h-5 w-5" /> },
  { id: 'technology', name: 'Technology', icon: <span>💻</span> },
  { id: 'business', name: 'Business', icon: <span>💼</span> },
  { id: 'sports', name: 'Sports', icon: <span>⚽</span> },
  { id: 'entertainment', name: 'Entertainment', icon: <span>🎬</span> },
  { id: 'health', name: 'Health', icon: <span>🏥</span> },
  { id: 'science', name: 'Science', icon: <span>🔬</span> },
  { id: 'africa', name: 'Africa', icon: <span>🌍</span> },
];

const FollowingPage = ({ onArticleClick }) => {
  const [followedSources, setFollowedSources] = useState(() => {
    const saved = localStorage.getItem('followedSources');
    return saved ? JSON.parse(saved) : ['bbc', 'ktpress'];
  });
  
  const [followedTopics, setFollowedTopics] = useState(() => {
    const saved = localStorage.getItem('followedTopics');
    return saved ? JSON.parse(saved) : ['world', 'technology'];
  });
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('followedSources', JSON.stringify(followedSources));
  }, [followedSources]);

  useEffect(() => {
    localStorage.setItem('followedTopics', JSON.stringify(followedTopics));
  }, [followedTopics]);

  // Fetch articles from followed topics
  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const responses = await Promise.all(
          followedTopics.map(topic =>
            fetch(`${BACKEND_URL}/api/articles/${topic}?page=0&limit=6`)
              .then(res => res.json())
              .catch(() => ({ articles: [] }))
          )
        );
        
        const allArticles = responses.flatMap(r => r.articles || []);
        // Shuffle and take first 20
        const shuffled = allArticles.sort(() => Math.random() - 0.5).slice(0, 20);
        setArticles(shuffled);
      } catch (error) {
        console.error('Failed to fetch following articles:', error);
      } finally {
        setLoading(false);
      }
    };

    if (followedTopics.length > 0) {
      fetchArticles();
    } else {
      setArticles([]);
      setLoading(false);
    }
  }, [followedTopics]);

  const toggleSource = (id) => {
    setFollowedSources(prev => 
      prev.includes(id) 
        ? prev.filter(s => s !== id)
        : [...prev, id]
    );
  };

  const toggleTopic = (id) => {
    setFollowedTopics(prev => 
      prev.includes(id) 
        ? prev.filter(t => t !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
          <Heart className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Following</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            News from sources and topics you follow
          </p>
        </div>
      </div>

      {/* Following Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sources Section */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2 mb-4">
            <Newspaper className="h-5 w-5 text-emerald-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Sources</h3>
            <span className="text-xs text-gray-500 ml-auto">{followedSources.length} following</span>
          </div>
          
          <div className="space-y-2">
            {AVAILABLE_SOURCES.map(source => (
              <button
                key={source.id}
                onClick={() => toggleSource(source.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                  followedSources.includes(source.id)
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-500'
                    : 'bg-gray-50 dark:bg-gray-800 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="text-xl">{source.icon}</span>
                <span className="flex-1 text-left font-medium text-gray-900 dark:text-white">
                  {source.name}
                </span>
                {followedSources.includes(source.id) ? (
                  <Check className="h-5 w-5 text-emerald-500" />
                ) : (
                  <Plus className="h-5 w-5 text-gray-400" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Topics Section */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5 text-emerald-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Topics</h3>
            <span className="text-xs text-gray-500 ml-auto">{followedTopics.length} following</span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_TOPICS.map(topic => (
              <button
                key={topic.id}
                onClick={() => toggleTopic(topic.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all text-sm font-medium ${
                  followedTopics.includes(topic.id)
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {topic.icon}
                <span>{topic.name}</span>
                {followedTopics.includes(topic.id) && (
                  <X className="h-4 w-4 ml-1" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Feed Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Your Feed
        </h2>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="animate-pulse bg-gray-100 dark:bg-gray-800 rounded-xl h-32" />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-2xl">
            <Heart className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              Follow some topics to see articles here
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {articles.map((article, idx) => (
              <div
                key={article.id || idx}
                onClick={() => onArticleClick(article)}
                className="flex gap-4 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <span className="font-medium">{article.source}</span>
                    <span>•</span>
                    <span>{article.category?.replace('_', ' ')}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2 text-sm">
                    {article.title}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {article.description}
                  </p>
                </div>
                {article.image_url && (
                  <img
                    src={article.image_url}
                    alt=""
                    className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowingPage;
